import { useEffect, useState, useCallback, useMemo } from 'react';

type UseCountdownTimerParams = {
  initialSeconds: number;
  onTimerStop: () => void;
};

type UseCountdownTimerReturn = {
  secondsLeft: number;
  progressPercentage: number;
  startTimer: () => void;
};

export function useCountdownTimer({ initialSeconds, onTimerStop }: UseCountdownTimerParams): UseCountdownTimerReturn {
  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);
  const [timerRunning, setTimerRunning] = useState(false);

  useEffect(() => {
    if (!timerRunning) {
      return undefined;
    }

    const interval = setInterval(() => {
      setSecondsLeft((prevSeconds) => {
        const newSeconds = prevSeconds - 1;

        // Handle timer completion inside the effect
        if (newSeconds <= 0) {
          setTimerRunning(false);
          onTimerStop();
          return 0;
        }

        return newSeconds;
      });
    }, 1_000);

    return () => clearInterval(interval);
  }, [timerRunning, onTimerStop]);

  const startTimer = useCallback(() => {
    setSecondsLeft(initialSeconds);
    setTimerRunning(true);
  }, [initialSeconds]);

  const progressPercentage = useMemo(() => ((initialSeconds - secondsLeft) / initialSeconds) * 100, [
    initialSeconds,
    secondsLeft,
  ]);

  return {
    secondsLeft,
    progressPercentage,
    startTimer,
  };
}
